import { FC, useContext, useMemo, useState } from 'react';
import { Info, Add, Article } from '@mui/icons-material';
import { Grid, Box, Typography, Button } from '@mui/material';
import {
  Page,
  PageTitleWithButton,
  Loader,
  Card,
  Button as SMButton,
  BuildNotes,
  ExternalLink
} from '../../../../components';
import { useQuery } from 'react-query';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useParams } from 'react-router-dom';
import { getContractDetails } from '../../../../fetch';
import { IContractDetails, UserRoles } from '../../../../models';
import { ContractAcceptanceStatusDetail } from '../contract-acceptance-status-detail';
import { AddTodoModal } from './add-todo-modal';
import { UserContext } from '../../../../context';
import { useFlags } from '../../../../hooks';
const { LegacySystem } = require('../../../../buildSettings.json');

interface IContractAcceptanceDetail {}

export const ContractAcceptanceDetail: FC<IContractAcceptanceDetail> = () => {
  const classes = useStyles();
  const { v2Jobs } = useFlags();
  const [isTodoModalOpen, setTodoModalOpen] = useState(false);
  const { buildId }: { buildId: string } = useParams();

  const userContext = useContext(UserContext);

  const {
    isLoading: isLoadingContractDetails,
    refetch: refetchContractDetails,
    data: contractDetails
  } = useQuery<IContractDetails, Error>(['getContractDetails'], () => getContractDetails(buildId));

  // this permission matches the userRoles given to the "Jobs" development module here links.tsx
  // also wrap with FF
  const canSeeJobDetails = useMemo(() => {
    return (
      userContext?.user?.userRoles?.includes(UserRoles.BLAdmin) ||
      userContext?.user?.userRoles?.includes(UserRoles.BLDevelopment)
    );
  }, [userContext]);

  const canSeeContractDetails = useMemo(() => {
    if (
      userContext.user?.userRoles?.includes(UserRoles.BLAdmin) ||
      userContext.user?.userRoles?.includes(UserRoles.BLSalesAdmin) ||
      userContext.user?.userRoles?.includes(UserRoles.BLSalesCoordinator)
    ) {
      return true;
    }
    if (
      userContext.user?.userRoles?.includes(UserRoles.BLAgent) ||
      userContext.user?.userRoles?.includes(UserRoles.BLSales)
    ) {
      return false;
    }
    return true;
  }, [userContext]);

  const title = contractDetails ? `${contractDetails?.contractName}` : 'Loading...';
  return (
    <>
      <Page title={'Contract Information'}>
        <PageTitleWithButton
          pageTitleBackProps={{
            title: title,
            backText: 'Back to Contract Acceptance',
            backUrl: '/sales/contracting/contract-acceptance'
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => {
                setTodoModalOpen(true);
              }}
              color="secondary"
              variant="contained"
            >
              Add Todo
            </Button>
          }
        />
        <Box mt={1}>
          <Card title="Contract Information" icon={<Info />}>
            {!isLoadingContractDetails && (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography className={classes.readOnlyHeaders} variant="h6">
                        Purchaser Info
                      </Typography>
                      <Typography variant="subtitle1">
                        {contractDetails?.contractName}, {contractDetails?.clientPhone},{' '}
                        {contractDetails?.clientEmail}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography className={classes.readOnlyHeaders} variant="h6">
                        Build Info
                      </Typography>
                      <Typography variant="subtitle1">
                        {contractDetails?.neighborhoodPhaseLot}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SMButton
                        id="build-details-link"
                        color="secondary"
                        variant="contained"
                        asLink
                        to={`/sales/contracting/contract-acceptance/build-details/${buildId}?redirect=/sales/contracting/contract-acceptance/${buildId}`}
                      >
                        Build Details
                      </SMButton>
                      {canSeeJobDetails && (
                        <>
                          {v2Jobs ? (
                            <SMButton
                              className={classes.jobDetailsButton}
                              id="job-details-link"
                              color="secondary"
                              variant="contained"
                              asLink
                              to={`/development/jobs/${contractDetails?.jobId}/general-information`}
                            >
                              Job Details
                            </SMButton>
                          ) : (
                            <Button
                              className={classes.jobDetailsButton}
                              color="secondary"
                              variant="contained"
                            >
                              <ExternalLink
                                to={`${LegacySystem}/Portals/Development/JobDetails.aspx?jobId=${contractDetails?.jobCode}`}
                                type="white"
                                target="_blank"
                              >
                                Job Details
                              </ExternalLink>
                            </Button>
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {isLoadingContractDetails && (
              <Box
                mt={1}
                minHeight="8rem"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Loader type="inline" position="centered">
                  Loading...
                </Loader>
              </Box>
            )}
          </Card>
        </Box>
        {canSeeContractDetails && (
          <ContractAcceptanceStatusDetail
            status={contractDetails?.status}
            statusText={contractDetails?.statusText}
            refetchContractDetails={refetchContractDetails}
          />
        )}
        <Box mt={1}>
          <Card title="Notes" icon={<Article />}>
            <BuildNotes buildId={buildId} />
          </Card>
        </Box>
        <AddTodoModal
          open={isTodoModalOpen}
          handleClose={(shouldClose = true) => {
            if (shouldClose) setTodoModalOpen(false);
          }}
          buildId={buildId}
        />
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  contractInfo: {
    fontWeight: '500',
    paddingRight: 8
  },
  readOnlyHeaders: {
    paddingBottom: theme.spacing(0.25),
    fontWeight: '700'
  },
  notSelected: {
    color: '#88888',
    opacity: 0.6
  },
  buttonText: {
    color: 'white'
  },
  jobDetailsButton: {
    marginLeft: '5px'
  }
}));
